import React, { useState, useRef, useEffect, useMemo } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { axiosWithoutInterceptors } from '../routes/axiosInstance';
import FormInput from '../components/FormInput';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import { COUNTRY_DIAL_CODES, DEFAULT_DIAL_CODE } from '../data/countryDialCodes';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_LOCAL_RE = /^[0-9 .()-]+$/;

function DialCodeCombobox({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => COUNTRY_DIAL_CODES.find((c) => c.dial === value) || COUNTRY_DIAL_CODES[0],
    [value]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRY_DIAL_CODES;
    return COUNTRY_DIAL_CODES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.dial.replace('+', '').includes(q.replace('+', ''))
    );
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const pick = (dial) => {
    onChange({ target: { value: dial } });
    setOpen(false);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Country code"
        className="shrink-0 w-[6.5rem] inline-flex items-center justify-between rounded-l-md rounded-r-none border-[0.5px] border-r-0 border-gray-300 dark:border-white/20 bg-white/5 dark:bg-white/5 pl-2 pr-2 py-2 text-sm text-gray-900 dark:text-white/90 focus:z-10 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
      >
        <span className="truncate">{selected.code} {selected.dial}</span>
        <ChevronDownIcon className={`h-4 w-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 left-0 w-72 rounded-md border border-gray-200 dark:border-white/10 bg-surface dark:bg-gray-870 shadow-lg">
          <div className="p-2 border-b border-gray-100 dark:border-white/10">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country or code…"
              className="w-full rounded-md border-[0.5px] border-gray-300 dark:border-white/20 bg-surface dark:bg-gray-870 px-2 py-1.5 text-sm text-gray-900 dark:text-white/90 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>
          <ul role="listbox" className="max-h-64 overflow-y-auto py-1 text-sm">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-gray-500 dark:text-gray-400">No matches</li>
            ) : (
              filtered.map((c) => {
                const active = c.dial === value;
                return (
                  <li key={`${c.code}-${c.dial}`}>
                    <button
                      type="button"
                      onClick={() => pick(c.dial)}
                      className={`w-full text-left px-3 py-1.5 flex items-center justify-between gap-3 transition ${
                        active
                          ? 'bg-brand-50 text-brand-700 dark:bg-brand-600/20 dark:text-white'
                          : 'text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-white/5'
                      }`}
                    >
                      <span className="truncate">
                        <span className="text-xs font-mono text-gray-500 dark:text-gray-400 mr-2">{c.code}</span>
                        {c.name}
                      </span>
                      <span className="shrink-0 font-mono tabular-nums text-gray-600 dark:text-gray-300">{c.dial}</span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

const initialForm = {
  applicant_type: 'individual',
  name: '',
  email: '',
  phone_dial: DEFAULT_DIAL_CODE,
  phone_local: '',
  organization: '',
  website: '',
  linkedin_url: '',
  github_url: '',
  country: '',
  role: '',
  message: '',
  fax_number: '', // honeypot
};

const URL_RE = /^https?:\/\/.+/i;
const isLinkedIn = (v) => /linkedin\.com/i.test(v);
const isGitHub = (v) => /github\.com/i.test(v);

export default function ApplyToJoin() {
  usePageTitle('Apply to Join');
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const isOrg = form.applicant_type === 'organization';

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const validate = () => {
    if (form.name.trim().length < 2) return 'Please enter your full name.';
    if (!EMAIL_RE.test(form.email.trim())) return 'Please enter a valid email address.';
    if (form.country.trim().length === 0) return 'Please enter your country.';
    if (!isOrg) {
      const local = form.phone_local.trim();
      if (!local) return 'Please enter your phone number.';
      if (!PHONE_LOCAL_RE.test(local)) return 'Phone number contains invalid characters.';
      const digits = local.replace(/\D/g, '');
      if (digits.length < 6) return 'Phone number is too short.';
    }
    if (isOrg && form.organization.trim().length === 0)
      return 'Please enter the organization name.';
    if (form.website && !URL_RE.test(form.website.trim()))
      return 'Website must start with http:// or https://.';
    if (form.linkedin_url) {
      if (!URL_RE.test(form.linkedin_url.trim())) return 'LinkedIn URL must start with http:// or https://.';
      if (!isLinkedIn(form.linkedin_url)) return 'LinkedIn URL must be a linkedin.com address.';
    }
    if (form.github_url) {
      if (!URL_RE.test(form.github_url.trim())) return 'GitHub URL must start with http:// or https://.';
      if (!isGitHub(form.github_url)) return 'GitHub URL must be a github.com address.';
    }
    if (form.message.length > 2000) return 'Message is too long.';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const phone = !isOrg && form.phone_local.trim()
        ? `${form.phone_dial} ${form.phone_local.trim()}`
        : '';
      const payload = { ...form, phone };
      delete payload.phone_dial;
      delete payload.phone_local;
      await axiosWithoutInterceptors.post('/user/access-request', payload);
      setSubmitted(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        setError('Too many requests. Please try again later.');
      } else if (status === 400) {
        const data = err?.response?.data;
        if (data && typeof data === 'object') {
          const firstField = Object.keys(data)[0];
          const msg = Array.isArray(data[firstField]) ? data[firstField][0] : data[firstField];
          setError(msg || 'Please review the form and try again.');
        } else {
          setError('Please review the form and try again.');
        }
      } else {
        setError('Something went wrong. Please try again later.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface dark:bg-gray-870 text-slate-900 dark:text-gray-100 flex flex-col">
      <PublicNav/>

      <main className="flex-1 flex items-center justify-center w-full">
        <div className="max-w-2xl mx-auto px-6 py-6 w-full">
          {!submitted && (
            <div className="text-center">
              <h1 className="text-lg sm:text-xl font-bold leading-tight tracking-tight text-slate-900 dark:text-white">
                Want to forecast on Predico? <span className="italic text-teal-700 dark:text-teal-400">Apply to join</span>.
              </h1>
              <p className="mt-1 text-xs text-slate-600 dark:text-gray-300">
                Predico is invite-only. Tell us a bit about yourself and your forecasting background, and we'll get back to you with next steps.
              </p>
            </div>
          )}

          {submitted ? (
            <div className="mt-10 rounded-2xl bg-surface/70 dark:bg-gray-870/60 backdrop-blur-sm border border-slate-900/10 dark:border-gray-100/10 p-6 sm:p-8 text-center">
              <p className="text-base font-semibold text-slate-900 dark:text-white">
                Thank you.
              </p>
              <p className="mt-2 text-sm text-slate-600 dark:text-gray-300">
                Your request has been received — we will be in touch shortly.
              </p>
              <div className="mt-6">
                <Link to="/"
                      className="inline-flex items-center gap-1.5 text-sm text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white transition">
                  <ArrowLeftIcon className="h-4 w-4"/>
                  Back to homepage
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="mt-4 rounded-2xl bg-surface/70 dark:bg-gray-870/60 backdrop-blur-sm border border-slate-900/10 dark:border-gray-100/10 p-4 sm:p-5">
                <form onSubmit={handleSubmit} className="space-y-2.5" noValidate>
                  {/* Honeypot */}
                  <div
                    aria-hidden="true"
                    style={{ position: 'absolute', left: '-10000px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}
                  >
                    <label htmlFor="fax_number">Fax number</label>
                    <input
                      id="fax_number"
                      name="fax_number"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.fax_number}
                      onChange={update('fax_number')}
                    />
                  </div>

                  <fieldset>
                    <legend className="block text-xs font-medium text-slate-700 dark:text-gray-300 mb-1.5">
                      I am applying as<span className="text-red-500">*</span>
                    </legend>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { value: 'individual', label: 'Individual' },
                        { value: 'organization', label: 'Organization' },
                      ].map((opt) => (
                        <label
                          key={opt.value}
                          className={`flex items-center justify-center cursor-pointer rounded-md border px-3 py-1.5 text-sm font-medium transition ${
                            form.applicant_type === opt.value
                              ? 'border-teal-700 bg-teal-50 text-teal-800 dark:bg-teal-400/10 dark:border-teal-400 dark:text-teal-300'
                              : 'border-slate-900/15 dark:border-gray-100/15 text-slate-700 dark:text-gray-300 hover:border-slate-900/30 dark:hover:border-gray-100/30'
                          }`}
                        >
                          <input
                            type="radio"
                            name="applicant_type"
                            value={opt.value}
                            checked={form.applicant_type === opt.value}
                            onChange={update('applicant_type')}
                            className="sr-only"
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="name" className="block text-xs font-medium text-slate-700 dark:text-gray-300">
                        {isOrg ? 'Contact name' : 'Full name'}<span className="text-red-500">*</span>
                      </label>
                      <FormInput id="name" name="name" type="text" autoComplete="name" variant="glass" required
                                 value={form.name} onChange={update('name')} maxLength={150} />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs font-medium text-slate-700 dark:text-gray-300">
                        Email<span className="text-red-500">*</span>
                      </label>
                      <FormInput id="email" name="email" type="email" autoComplete="email" variant="glass" required
                                 value={form.email} onChange={update('email')} maxLength={254} />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {isOrg ? (
                      <div>
                        <label htmlFor="organization" className="block text-xs font-medium text-slate-700 dark:text-gray-300">
                          Organization<span className="text-red-500">*</span>
                        </label>
                        <FormInput id="organization" name="organization" type="text" autoComplete="organization" variant="glass"
                                   required
                                   value={form.organization} onChange={update('organization')} maxLength={200}
                                   placeholder="e.g. INESC TEC" />
                      </div>
                    ) : (
                      <div className="min-w-0">
                        <label htmlFor="phone_local" className="block text-xs font-medium text-slate-700 dark:text-gray-300">
                          Phone<span className="text-red-500">*</span>
                        </label>
                        <div className="flex">
                          <DialCodeCombobox value={form.phone_dial} onChange={update('phone_dial')} />
                          <input
                            id="phone_local"
                            name="phone_local"
                            type="tel"
                            inputMode="tel"
                            autoComplete="tel-national"
                            required
                            value={form.phone_local}
                            onChange={update('phone_local')}
                            maxLength={32}
                            placeholder="912 345 678"
                            className="flex-1 min-w-0 rounded-r-md rounded-l-none border-[0.5px] border-gray-300 dark:border-white/20 bg-white/5 dark:bg-white/5 placeholder-gray-400 dark:placeholder-white/60 px-3 py-2 text-sm text-gray-900 dark:text-white/90 focus:z-10 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                          />
                        </div>
                      </div>
                    )}
                    <div className="min-w-0">
                      <label htmlFor="country" className="block text-xs font-medium text-slate-700 dark:text-gray-300">
                        Country<span className="text-red-500">*</span>
                      </label>
                      <FormInput id="country" name="country" type="text" autoComplete="country-name" variant="glass"
                                 required
                                 value={form.country} onChange={update('country')} maxLength={80} />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="min-w-0">
                      <label htmlFor="linkedin_url" className="block text-xs font-medium text-slate-700 dark:text-gray-300">
                        LinkedIn URL{isOrg ? ' (contact)' : ''}
                      </label>
                      <FormInput id="linkedin_url" name="linkedin_url" type="url" variant="glass"
                                 value={form.linkedin_url} onChange={update('linkedin_url')} maxLength={300}
                                 placeholder="https://linkedin.com/in/your-profile" />
                    </div>
                    {isOrg ? (
                      <div>
                        <label htmlFor="role" className="block text-xs font-medium text-slate-700 dark:text-gray-300">
                          Your role
                        </label>
                        <FormInput id="role" name="role" type="text" variant="glass"
                                   value={form.role} onChange={update('role')} maxLength={80}
                                   placeholder="e.g. Head of Trading" />
                      </div>
                    ) : (
                      <div>
                        <label htmlFor="github_url" className="block text-xs font-medium text-slate-700 dark:text-gray-300">
                          GitHub URL
                        </label>
                        <FormInput id="github_url" name="github_url" type="url" variant="glass"
                                   value={form.github_url} onChange={update('github_url')} maxLength={300}
                                   placeholder="https://github.com/your-handle" />
                      </div>
                    )}
                  </div>

                  {!isOrg && (
                    <div>
                      <label htmlFor="role" className="block text-xs font-medium text-slate-700 dark:text-gray-300">
                        Role / use case
                      </label>
                      <FormInput id="role" name="role" type="text" variant="glass"
                                 value={form.role} onChange={update('role')} maxLength={80}
                                 placeholder="e.g. Forecaster, Researcher" />
                    </div>
                  )}

                  <div>
                    <label htmlFor="message" className="block text-xs font-medium text-slate-700 dark:text-gray-300">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={2}
                      maxLength={2000}
                      value={form.message}
                      onChange={update('message')}
                      className="mt-1 block w-full rounded-md border border-slate-900/15 dark:border-gray-100/15 bg-white/60 dark:bg-gray-870/40 px-3 py-2 text-sm text-slate-900 dark:text-white shadow-sm focus:border-teal-700 dark:focus:border-teal-400 focus:outline-none focus:ring-1 focus:ring-teal-700 dark:focus:ring-teal-400"
                      placeholder={isOrg
                        ? "Tell us about your organization's interest in Predico, which markets or assets you want to forecast, your team's technical background, and any timeline."
                        : "Tell us about your forecasting/ML background and what you'd like to do on Predico, relevant experience, projects, and which markets interest you."}
                    />
                    <p className="mt-0.5 text-[11px] text-slate-500 dark:text-gray-400 text-right">{form.message.length}/2000</p>
                  </div>

                  {error && <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="mt-2 flex w-full justify-center rounded-md bg-slate-900 dark:bg-white text-white dark:text-gray-900 px-4 py-2 text-sm font-semibold shadow hover:opacity-90 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-teal-700 dark:focus:ring-teal-400"
                  >
                    {submitting ? 'Sending…' : 'Submit request'}
                  </button>
                  <p className="text-center text-xs text-slate-500 dark:text-gray-400">
                    We use these details to assess your application. See our{' '}
                    {/* New tab: the form lives in component state and navigating away would lose it. */}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-900 dark:hover:text-white transition">Privacy Policy</a>.
                  </p>
                </form>
              </div>

              <div className="mt-4 text-center">
                <Link to="/"
                      className="inline-flex items-center gap-1.5 text-sm text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white transition">
                  <ArrowLeftIcon className="h-4 w-4"/>
                  Back to homepage
                </Link>
              </div>
            </>
          )}
        </div>
      </main>

      <PublicFooter/>
    </div>
  );
}
